import { ArrowUpRight, Check, Star, Smile, MessageSquare, BarChart3, TrendingUp, Target, Zap, ThumbsUp, Heart, Shield, Clock, PieChart, LineChart, Filter, Download, Users, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import { useState } from 'react';

const features = [
  { icon: Star, title: 'CSAT Score Tracking', text: 'Track Customer Satisfaction Score on a 1-5 star scale. Monitor trends over time and identify satisfaction patterns across your team.' },
  { icon: Target, title: 'NPS Monitoring', text: 'Measure Net Promoter Score from -100 to 100. Track promoters, passives, and detractors to gauge customer loyalty.' },
  { icon: Smile, title: 'Sentiment Analysis', text: 'Automatically analyze feedback sentiment using keyword-based detection. Categorize responses as positive, neutral, or negative.' },
  { icon: BarChart3, title: 'Resolution Metrics', text: 'Track resolution rates, average resolution time, and reopen rates. Identify bottlenecks and improve customer experience.' },
  { icon: LineChart, title: 'Trend Analytics', text: 'View CSAT, NPS, and sentiment trends over time. Compare performance across different periods and channels.' },
  { icon: Filter, title: 'Channel-Based Insights', text: 'Analyze feedback by channel including tickets, widget chats, and unified conversations. See which channels perform best.' },
];

const benefits = [
  { title: 'Complete feedback visibility', text: 'Aggregate feedback from tickets, live chat, and unified conversations in one dashboard. No more scattered data across multiple tools.' },
  { title: 'Owner and admin access control', text: 'Secure feedback analytics restricted to workspace owners and admins. Protect sensitive customer data while empowering leadership.' },
  { title: 'Real-time sentiment insights', text: 'Instant sentiment analysis on every feedback submission. Detect unhappy customers early and take proactive action.' },
  { title: 'Multi-channel feedback tracking', text: 'Collect and analyze feedback from tickets, widget chats, and unified conversations. See the complete picture of customer satisfaction.' },
];

const useCases = [
  { title: 'Post-resolution customer surveys', text: 'Automatically send CSAT surveys after ticket resolution. Collect ratings and feedback on support quality and resolution speed.' },
  { title: 'Live chat satisfaction tracking', text: 'Capture instant feedback after live chat sessions. Let customers rate their experience with a simple star rating.' },
  { title: 'Unified conversation feedback', text: 'Collect feedback from all channels in one place. WhatsApp, Instagram, Telegram, and live chat feedback unified.' },
  { title: 'Agent performance coaching', text: 'Use CSAT and sentiment data to coach your team. Identify top performers and areas for improvement with data-driven insights.' },
];

const industries = [
  { title: 'E-commerce & Retail', text: 'Track satisfaction on order support, returns, and delivery issues. Improve CSAT and reduce returns with better support.' },
  { title: 'SaaS & Technology', text: 'Monitor NPS and CSAT for technical support. Identify churn risks early with sentiment analysis and trend tracking.' },
  { title: 'Financial Services', text: 'Track satisfaction on account issues, transactions, and support requests. Maintain high CSAT for regulatory compliance.' },
  { title: 'Healthcare & Medical', text: 'Measure patient satisfaction on appointment support and inquiries. Ensure high-quality service with feedback tracking.' },
];

const metrics = [
  { tier: 'CSAT Score', description: 'Average customer satisfaction rating on a 1-5 star scale', value: '4.8/5', icon: Star },
  { tier: 'NPS Score', description: 'Net Promoter Score measuring customer loyalty', value: '85', icon: Target },
  { tier: 'Resolution Rate', description: 'Percentage of cases successfully resolved', value: '95%', icon: CheckCircle },
  { tier: 'Avg Resolution Time', description: 'Average time to resolve customer issues', value: '15m', icon: Zap },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'What is CSAT and how is it calculated?',
    answer: 'CSAT (Customer Satisfaction Score) is calculated by averaging customer ratings on a 1-5 star scale. Customers rate their experience after ticket resolution or live chat, giving you a clear measure of satisfaction.',
  },
  {
    question: 'What is NPS and why does it matter?',
    answer: 'NPS (Net Promoter Score) measures customer loyalty on a scale of -100 to 100. It categorizes customers as promoters (9-10), passives (7-8), or detractors (0-6). High NPS correlates with customer retention and referrals.',
  },
  {
    question: 'How does sentiment analysis work?',
    answer: 'Our sentiment analysis uses keyword-based detection to categorize feedback as positive, neutral, or negative. It analyzes feedback text for positive words like "great", "excellent", "helpful" and negative words like "poor", "slow", "frustrating".',
  },
  {
    question: 'What feedback sources are included?',
    answer: 'Feedback Analytics aggregates data from three sources: business tickets (post-resolution ratings), widget live chat (instant feedback), and unified conversations (multi-channel feedback). All sources are unified in one dashboard.',
  },
  {
    question: 'Who can access feedback analytics?',
    answer: 'Feedback Analytics is restricted to workspace owners and admins only. This ensures sensitive customer feedback data is protected while empowering leadership to make data-driven decisions.',
  },
  {
    question: 'Can I export feedback data?',
    answer: 'Yes. You can export all feedback data as CSV including ratings, feedback text, sentiment, channel, customer information, and timestamps. This is useful for external reporting and deeper analysis.',
  },
  {
    question: 'How do I track resolution metrics?',
    answer: 'Resolution metrics track the percentage of resolved cases, average resolution time, and reopen rates. These metrics help you identify bottlenecks, measure team efficiency, and improve customer experience.',
  },
  {
    question: 'Can I filter feedback by channel?',
    answer: 'Yes. You can filter feedback by channel including All Channels, Widget (live chat), and Tickets. This helps you understand which channels perform best and where to focus improvement efforts.',
  },
];

export default function CsatFeedback() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  return (
    <>
      <SEO
        title="CSAT & Feedback Analytics | Track Customer Satisfaction and NPS"
        description="Comprehensive CSAT and feedback analytics with NPS tracking, sentiment analysis, resolution metrics, and multi-channel insights. Perfect for customer service teams."
        path="/csat-feedback"
        keywords="CSAT, customer satisfaction, NPS, net promoter score, feedback analytics, sentiment analysis, customer feedback, resolution metrics, CSAT tracking, NPS monitoring"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 text-sm font-semibold text-blue-300 backdrop-blur">
                <CheckCircle className="h-4 w-4" /> CSAT • NPS • Sentiment Analysis
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                CSAT & <span className="gradient-text">Feedback Analytics</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Track customer satisfaction with CSAT scores, NPS monitoring, sentiment analysis, and resolution metrics. Aggregate feedback from tickets, live chat, and unified conversations in one dashboard.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="#features"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-blue-500 hover:bg-slate-800"
                >
                  Explore Features <BarChart3 className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-blue-400/10 blur-3xl" />
              <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/60 p-2 backdrop-blur">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/HeroCST.webp"
                  alt="CSAT and feedback analytics dashboard showing metrics and trends"
                  className="rounded-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section id="features" className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Comprehensive feedback analytics</h2>
            <p className="section-subtitle mx-auto mt-4">Professional features designed to measure and improve customer satisfaction.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="metrics-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="metrics-heading" className="section-title">Key metrics at a glance</h2>
            <p className="section-subtitle mx-auto mt-4">Track the most important customer satisfaction metrics in real time.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {metrics.map((m) => (
              <div key={m.tier} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm text-center">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600 mx-auto">
                  <m.icon className="h-6 w-6" />
                </div>
                <p className="text-3xl font-extrabold text-slate-900">{m.value}</p>
                <h3 className="mt-2 text-base font-bold text-slate-900">{m.tier}</h3>
                <p className="mt-2 text-sm text-slate-600">{m.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why teams choose feedback analytics</h2>
            <p className="section-subtitle mx-auto mt-4">Advantages that set our feedback analytics apart from basic surveys.</p>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {benefits.map((b) => (
              <div key={b.title} className="flex gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
                  <Check className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{b.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="use-cases-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="use-cases-heading" className="section-title">Feedback analytics use cases</h2>
            <p className="section-subtitle mx-auto mt-4">Practical applications of feedback analytics across your business.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <MessageSquare className="h-6 w-6" />
                </div>
                <h3 className="text-base font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="industries-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="industries-heading" className="section-title">Industries powered by feedback analytics</h2>
            <p className="section-subtitle mx-auto mt-4">From e-commerce to healthcare, businesses across all industries use feedback analytics.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {industries.map((i) => (
              <div key={i.title} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <Users className="h-6 w-6" />
                </div>
                <h3 className="text-base font-bold text-slate-900">{i.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{i.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about CSAT and feedback analytics.</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.question} className="rounded-2xl border border-slate-200 bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === faq.question ? null : faq.question)}
                  className="flex w-full items-center justify-between p-6 text-left"
                >
                  <span className="text-base font-semibold text-slate-900">{faq.question}</span>
                  {openFaq === faq.question ? (
                    <ChevronUp className="h-5 w-5 text-slate-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-500" />
                  )}
                </button>
                {openFaq === faq.question && (
                  <div className="px-6 pb-6">
                    <p className="text-sm text-slate-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-24 text-white">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
            Ready to improve customer satisfaction?
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Start tracking CSAT, NPS, and sentiment today with comprehensive feedback analytics.
          </p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:justify-center">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              Get Started Free <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
